"use client";

import React, { useEffect, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderNav({
  isMobileOpen,
  setIsMobileOpen
}: {
  isMobileOpen: boolean;
  setIsMobileOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-6 w-16 animate-pulse rounded-md bg-muted" />
        <div className="h-6 w-16 animate-pulse rounded-md bg-muted" />
        <div className="h-6 w-16 animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <>
      {}
      <div className="hidden md:flex items-center">
        <NavigationMenu viewport={false}>
          <NavigationMenuList className="gap-6">
            {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                <NavigationMenuLink asChild>
                  <Link to={item.href || "#"} className="relative py-2 text-lg font-medium text-foreground/90 transition-colors hover:text-primary after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:origin-bottom-right after:scale-x-0 after:bg-primary after:transition-transform after:duration-300 hover:after:origin-bottom-left hover:after:scale-x-100">
                    {item.label}
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>)}
          </NavigationMenuList>
        </NavigationMenu>
      </div>

      {}
      {isMobileOpen && <div className="absolute left-0 top-full w-full border-b border-border bg-background/95 p-4 shadow-xl backdrop-blur-[12px] md:hidden flex flex-col gap-2">
          {menuItems.map((item, i) => <div key={item.id} data-index={i}>
              <Link to={item.href || "#"} className="block border-b border-border/50 py-3 text-lg font-medium text-foreground transition-colors hover:text-primary" onClick={() => setIsMobileOpen(false)}>
                {item.label}
              </Link>
            </div>)}
          <div className="mt-4">
            <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-lg h-14 shadow-[0_0_15px_rgba(132,255,0,0.2)]" asChild>
              <Link to="tel:778-385-3007" onClick={() => setIsMobileOpen(false)}>
                <Phone className="mr-2 h-5 w-5 animate-pulse" />
                CALL 778-385-3007
              </Link>
            </Button>
          </div>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("dark bg-background text-foreground sticky top-0 z-50 w-full transition-all duration-300", isScrolled ? "bg-background/80 backdrop-blur-[12px] border-b border-border shadow-md" : "border-b border-transparent")}>
      <div className="container mx-auto flex h-20 items-center justify-between px-4">
        {}
        <Link to="/" className="relative z-50 flex shrink-0 items-center">
          <img src="https://proud-porpoise.10web.cloud/wp-content/uploads/2026/03/cropped-cropped-cropped-cropped-JUNK-CAR-LOGO-3.png" alt="Junk Car Pick Up BC" className="h-20 w-auto" />
        </Link>

        {}
        <MenuProvider menu_id="45" className="flex flex-1 items-center justify-end gap-4 md:gap-8">
          <HeaderNav isMobileOpen={isMobileOpen} setIsMobileOpen={setIsMobileOpen} />

          {}
          <Button className="hidden md:flex bg-primary text-primary-foreground hover:bg-primary/90 font-bold shadow-[0_0_15px_rgba(132,255,0,0.2)] hover:shadow-[0_0_25px_rgba(132,255,0,0.4)] transition-all duration-300" asChild>
            <Link to="tel:778-385-3007">
              <Phone className="mr-2 h-4 w-4 animate-pulse" />
              CALL 778-385-3007
            </Link>
          </Button>

          {}
          <Button variant="ghost" size="icon" className="relative z-50 md:hidden text-foreground hover:text-primary" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-label="Toggle menu">
            {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </MenuProvider>
      </div>
    </header>;
}